import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption,
} from '@ooh/ui/components/searchable-select-field';
import { Badge } from '@ooh/ui/components/badge';

export interface ColumnMappingRow {
  id: string;
  fieldLabel: string;
  value: string;
  onChange: (next: string) => void;
  sampleValue: string | null;
  testId?: string;
  /** True for fallback fields (Adres/Miasto) that are not required for the
   * import to work — renders a small "optional" tag next to the field label
   * so an empty mapping reads as "not applicable", not "you forgot this". */
  optional?: boolean;
}

export interface ColumnMappingTableLabels {
  systemFieldHeader: string;
  fileColumnHeader: string;
  sampleValueHeader: string;
  columnPlaceholder?: string;
  sampleEmptyPlaceholder: string;
  optionalTag: string;
  /** Label for the clear/empty option prepended to every row's dropdown,
   * letting the operator undo an accidental column pick back to unmapped. */
  clearOptionLabel: string;
}

export interface ColumnMappingTableProps {
  rows: ColumnMappingRow[];
  columnOptions: SearchableSelectFieldOption[];
  labels: ColumnMappingTableLabels;
  testIdPrefix?: string;
}

/**
 * Explicit "Pole w systemie | Kolumna w pliku | Przykład z pliku" table
 * replacing the earlier label-over-dropdown layout, which a real user found
 * structurally ambiguous (unclear whether the label named our field or
 * asserted the dropdown's value). The header row states the mapping
 * direction unambiguously; each row's sample-value cell lets the operator
 * confirm a column pick against a real value from the file. See
 * `mapping-phase-table`-style tables for the same "explicit header states
 * direction" precedent (that feature's row semantics are mirrored, not
 * copied — file-columns-as-rows there vs. system-fields-as-rows here).
 */
export function ColumnMappingTable({
  rows,
  columnOptions,
  labels,
  testIdPrefix = 'column-mapping',
}: ColumnMappingTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border" data-testid={testIdPrefix}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{labels.systemFieldHeader}</TableHead>
            <TableHead>{labels.fileColumnHeader}</TableHead>
            <TableHead>{labels.sampleValueHeader}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} listRow>
              <TableCell className="px-4 py-2 text-xs">
                <span className="flex items-center gap-2">
                  {row.fieldLabel}
                  {row.optional ? (
                    <Badge
                      variant="outline"
                      className="px-1.5 py-0 text-2xs font-normal text-muted-foreground"
                    >
                      {labels.optionalTag}
                    </Badge>
                  ) : null}
                </span>
              </TableCell>
              <TableCell className="px-4 py-2">
                <SearchableSelectField
                  id={`${testIdPrefix}-${row.id}-select`}
                  options={columnOptions}
                  value={row.value}
                  onChange={row.onChange}
                  placeholder={labels.clearOptionLabel}
                  searchPlaceholder={labels.columnPlaceholder}
                  allowEmpty
                  emptyLabel={labels.clearOptionLabel}
                  error={!row.optional && row.value === ''}
                  data-testid={row.testId ?? `${testIdPrefix}-${row.id}-select`}
                />
              </TableCell>
              <TableCell className="max-w-48 truncate px-4 py-2 text-xs text-muted-foreground">
                {row.sampleValue ?? labels.sampleEmptyPlaceholder}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
